import { type Dispatch, type SetStateAction } from 'react';
import {
  declaredAbsentInstancesAt,
  pinnedInstance,
  type EquipmentDefault,
  type Exercise,
  type LocationUnavailable,
} from '@jackedlogs/shared';

import { resolveSeededLoadedSides } from './state/bilateralPreference';
import { uuidv4 } from './uuid';
import type { LoggerDispatch } from './state/loggerDraft';
import type { LoggerDraft, WeightUnit } from './state/loggerDraftTypes';
import {
  getCachedHistoryForSlug,
  getSilentInstanceForSlug,
} from './useEntryHistory';
import { draftSetsFromHistoryEntry } from './workoutMapping';
import { catalogLoadedSides, deriveEntryKind } from './deriveEntryKind';

// Add-exercise handlers (Track R + the unified picker, inbox 963ca6f2).
// `onPick` adds one exercise (legacy single path). `addMany` adds N standalone
// (the Esercizi-tab multi-select). `createSuperset` adds new members + groups
// them with the current entry + any picked already-present entries in one shot
// (the Superset tab). All share `addOne`, which dispatches add_exercise then
// seeds laterality + history prefill — the freshly-appended entry's index is
// passed in explicitly (`atIdx`) because draftRef doesn't advance between
// queued dispatches in a loop.
export function useAddExercise({
  dispatch,
  defaultUnit,
  setPickerOpen,
  draftRef,
  catalog,
  locationUnavailable,
  equipmentDefault,
}: {
  dispatch: LoggerDispatch;
  defaultUnit: WeightUnit;
  setPickerOpen: Dispatch<SetStateAction<boolean>>;
  draftRef: { current: LoggerDraft };
  catalog: Exercise[] | null;
  /** EQ-1-GYM-COLDSTART R6 — the athlete's declared absences, off the host
   *  profile. Read here so the silent apply below cannot attach a machine
   *  that was declared not to be in the room. */
  locationUnavailable?: LocationUnavailable;
  /** EQ-1-PIN-DEFAULT — the athlete's elected machine per (place, exercise). */
  equipmentDefault?: EquipmentDefault;
}): {
  onPick: (ex: Exercise) => void;
  addMany: (slugs: string[]) => void;
  createSuperset: (args: {
    addSlugs: string[];
    pairEntryIdxs: number[];
    name: string;
    /** When set, add the new members to this EXISTING group (preserving its
     *  name/kind) instead of minting a new one — the "Add exercise to
     *  superset" menu action. */
    targetGroupId?: string;
  }) => void;
} {
  const addOne = (ex: Exercise, atIdx: number): void => {
    // EQ-1 — the add path NEVER asks which machine (Antenore, 2026-08-13).
    // One known instance is applied in silence; with two or more the entry is
    // added WITHOUT one and the athlete picks from replace, the single place
    // the question is ever raised. Asking here would turn a multi-select add
    // into a queue of prompts, which is what `one tap raises one prompt`
    // forbids. Resolved one layer below `onAddMany` so its shape is untouched,
    // off the history window already in memory — no new fetch.
    //
    // ⭐ R6 — and it never attaches what the athlete declared absent HERE. This
    // is the path with no tap at all, so a wrong machine applied here is
    // invisible by construction: nothing on screen would say it happened. With
    // the only known machine declared missing, the entry is born with no tag
    // rather than with the wrong one. The place is read off the DRAFT, and it
    // is used for two things now: to look up a declaration, and — since
    // EQ-1-LASTHERE (2026-08-18) — to tell the resolver where the entry is
    // being born. ⛔ It is still never an ordering signal that hides anything;
    // the counter-explanation lives on getSilentInstanceForSlug.
    const place = draftRef.current.location ?? null;
    dispatch({
      type: 'add_exercise',
      exerciseId: ex.slug,
      // DraftEntry.exerciseName is a snapshot string; stamp the EN canonical.
      exerciseName: ex.name.en,
      ...deriveEntryKind(ex),
      defaultUnit,
      equipmentInstance: getSilentInstanceForSlug(
        ex.slug,
        declaredAbsentInstancesAt(locationUnavailable, place, ex.slug),
        // EQ-1-LASTHERE: the same place, now also handed to the resolver —
        // it may attach only a machine already used HERE.
        place,
        // EQ-1-PIN-DEFAULT — what the athlete elected here, if anything.
        pinnedInstance(equipmentDefault, place, ex.slug),
      ),
      // EQ-1 slice D — the session's place, inherited at birth. Read from the
      // ref rather than passed in: the picker adds N exercises in a loop and
      // the place is a property of the session, not of the pick.
      location: place,
    });

    // Prefill the placeholder with the last-logged session so a known
    // exercise opens with sensible values.
    const lastLogged = getCachedHistoryForSlug(ex.slug);
    if (lastLogged) {
      const sets = draftSetsFromHistoryEntry(
        lastLogged.entry,
        defaultUnit,
        catalogLoadedSides(ex),
      );
      if (sets.length > 0) {
        dispatch({ type: 'seed_new_entry_sets', entryIdx: atIdx, sets });
      }
    }
    if (ex.movementPattern !== 'cardio') {
      void resolveSeededLoadedSides(ex.slug, catalogLoadedSides(ex)).then(
        (sides) => {
          if (sides === 1) return;
          // ⛔ Correct the row we just added, addressed by the index we
          // already have — never by searching for its slug (LG-39). With the
          // same exercise already in the workout, that search matched the
          // EARLIER row and rewrote the loadedSides of sets the athlete had
          // already logged. The guard mirrors useReplaceExercise: if the row
          // at that index is no longer this exercise, the draft moved under
          // this async resolve and the correction is dropped.
          const target = draftRef.current.entries[atIdx];
          if (!target || target.exerciseId !== ex.slug) return;
          if (target.loadedSides === sides) return;
          dispatch({
            type: 'set_entry_loaded_sides',
            entryIdx: atIdx,
            loadedSides: sides,
          });
        },
      );
    }
  };

  // Resolve slugs → Exercise and add each one, returning the indices they land
  // at (base + running offset; draftRef stays at `base` across queued dispatches).
  const addResolved = (slugs: string[]): number[] => {
    const base = draftRef.current.entries.length;
    const idxs: number[] = [];
    for (const slug of slugs) {
      const ex = catalog?.find((e) => e.slug === slug);
      if (!ex) continue;
      const atIdx = base + idxs.length;
      addOne(ex, atIdx);
      idxs.push(atIdx);
    }
    return idxs;
  };

  return {
    onPick: (ex: Exercise) => {
      addOne(ex, draftRef.current.entries.length);
      setPickerOpen(false);
    },
    addMany: (slugs: string[]) => {
      addResolved(slugs);
    },
    createSuperset: ({ addSlugs, pairEntryIdxs, name, targetGroupId }) => {
      const newIdxs = addResolved(addSlugs);
      // Adding to an existing group: reuse its object (keeps name + kind);
      // otherwise mint a fresh superset with the (optional) given name.
      const existing = targetGroupId
        ? draftRef.current.entries.find((e) => e.group?.id === targetGroupId)
            ?.group
        : undefined;
      const group =
        existing ??
        {
          id: targetGroupId ?? uuidv4(),
          kind: 'superset' as const,
          ...(name.trim().length > 0 ? { name: name.trim() } : {}),
        };
      // ⛔ ONE dispatch, not one per entry (PE-20). Every index here was
      // resolved against the same snapshot (`addResolved` predicts them as
      // base+offset because draftRef does not advance between queued
      // dispatches), and set_group now re-orders the array to keep members
      // contiguous — so a loop would have the second iteration onwards
      // pointing at rows that moved under it.
      dispatch({
        type: 'set_group',
        entryIdxs: [...pairEntryIdxs, ...newIdxs],
        group,
      });
      // SS-3 (Antenore, 2026-08-24) — a FRESHLY minted superset aligns its
      // members to the one with the most sets, no prompt (work-by-default).
      // The padded rows are born EMPTY (`seed: 'empty'`), the opposite of
      // LG-36's inheriting ＋: the athlete fills them on purpose.
      // ⛔ Scoped to fresh mint only — the `targetGroupId` "add to existing
      // superset" path keeps its current (non-aligning) behaviour; SS-3
      // decided the CREATE gesture, not add-to-existing.
      if (!existing) {
        dispatch({
          type: 'align_group_sets',
          groupId: group.id,
          mode: 'pad',
          seed: 'empty',
          defaultUnit,
        });
      }
    },
  };
}
